import { LscApiError } from './api';
import {
  JOIN_BAD_INVITE_ASK_COMMISSIONER,
  JOIN_BAD_INVITE_HEADLINE,
  JOIN_COMMISSIONER_SECONDARY_NOTE,
  JOIN_GO_TO_LEAGUES,
  JOIN_INVITE_ALREADY_MEMBER_DETAIL,
  JOIN_INVITE_ALREADY_MEMBER_HEADLINE,
  JOIN_TRY_ANOTHER_CODE,
} from './joinMemberCopy';
import type { LscLeagueInvitePreviewResult } from './types';

export type JoinInviteRecoveryCta = {
  code: string;
  label: string;
  href?: string;
};

export type JoinInviteRecovery = {
  code: string;
  headline: string;
  message: string;
  primaryCta: JoinInviteRecoveryCta | null;
  secondaryCtas: JoinInviteRecoveryCta[];
  recoveryReasonCodes: string[];
  showCommissionerSecondary: boolean;
};

const RECOVERY_TRY_ANOTHER: JoinInviteRecoveryCta = {
  code: 'try_another',
  label: JOIN_TRY_ANOTHER_CODE,
  href: '/join',
};

const RECOVERY_LEAGUES: JoinInviteRecoveryCta = {
  code: 'leagues',
  label: JOIN_GO_TO_LEAGUES,
  href: '/leagues',
};

function buildRecovery(
  code: string,
  headline: string,
  message: string,
  options?: {
    primary?: JoinInviteRecoveryCta | null;
    secondary?: JoinInviteRecoveryCta[];
    reasonCodes?: string[];
    showCommissionerSecondary?: boolean;
  }
): JoinInviteRecovery {
  const secondary = options?.secondary ?? [RECOVERY_TRY_ANOTHER, RECOVERY_LEAGUES];
  return {
    code,
    headline,
    message,
    primaryCta: options?.primary ?? RECOVERY_TRY_ANOTHER,
    secondaryCtas: secondary,
    recoveryReasonCodes: options?.reasonCodes ?? [code],
    showCommissionerSecondary: options?.showCommissionerSecondary ?? true,
  };
}

function recoveryFromInviteStatus(status?: string): JoinInviteRecovery | null {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized || normalized === 'active') return null;

  if (normalized === 'revoked') {
    return buildRecovery(
      'invite_revoked',
      JOIN_BAD_INVITE_HEADLINE,
      'This invite was revoked by the commissioner. ' + JOIN_BAD_INVITE_ASK_COMMISSIONER
    );
  }
  if (normalized === 'expired') {
    return buildRecovery(
      'invite_expired',
      JOIN_BAD_INVITE_HEADLINE,
      'This invite has expired or has no remaining uses. ' + JOIN_BAD_INVITE_ASK_COMMISSIONER
    );
  }
  if (normalized === 'invalid') {
    return buildRecovery(
      'invite_invalid',
      JOIN_BAD_INVITE_HEADLINE,
      'This invite was not found or is no longer valid. ' + JOIN_BAD_INVITE_ASK_COMMISSIONER
    );
  }
  return buildRecovery(
    'invite_unavailable',
    JOIN_BAD_INVITE_HEADLINE,
    'This invite is not available right now. ' + JOIN_BAD_INVITE_ASK_COMMISSIONER
  );
}

/** Map invite preview payload to safe recovery UX when preview is invalid. */
export function resolveJoinInvitePreviewRecovery(
  preview: LscLeagueInvitePreviewResult | null | undefined
): JoinInviteRecovery | null {
  if (!preview) return null;

  if (preview.valid === true) {
    return null;
  }

  const fromStatus = recoveryFromInviteStatus(preview.invite_status);
  if (fromStatus) return fromStatus;

  return buildRecovery(
    'invite_preview_invalid',
    JOIN_BAD_INVITE_HEADLINE,
    'This invite is not valid, expired, revoked, or has no remaining uses. ' +
      JOIN_BAD_INVITE_ASK_COMMISSIONER
  );
}

/** Map API errors from preview/accept to safe recovery UX. */
export function resolveJoinInviteErrorRecovery(
  err: unknown,
  context: 'preview' | 'accept' = 'accept'
): JoinInviteRecovery {
  if (err instanceof LscApiError) {
    const code = String(err.code || '').trim().toLowerCase();
    if (code === 'invite_revoked') {
      return buildRecovery(
        'invite_revoked',
        JOIN_BAD_INVITE_HEADLINE,
        'This invite was revoked by the commissioner. ' + JOIN_BAD_INVITE_ASK_COMMISSIONER
      );
    }
    if (code === 'invite_expired') {
      return buildRecovery(
        'invite_expired',
        JOIN_BAD_INVITE_HEADLINE,
        'This invite has expired or has no remaining uses. ' + JOIN_BAD_INVITE_ASK_COMMISSIONER
      );
    }
    if (code === 'invite_invalid') {
      return buildRecovery(
        'invite_invalid',
        JOIN_BAD_INVITE_HEADLINE,
        'This invite was not found or is no longer valid. ' + JOIN_BAD_INVITE_ASK_COMMISSIONER
      );
    }
    if (code === 'league_archived') {
      return buildRecovery(
        'league_archived',
        JOIN_BAD_INVITE_HEADLINE,
        'This league is archived and cannot accept new members. ' +
          JOIN_BAD_INVITE_ASK_COMMISSIONER,
        { showCommissionerSecondary: false }
      );
    }
    if (code === 'already_member') {
      return buildRecovery(
        'already_member',
        JOIN_INVITE_ALREADY_MEMBER_HEADLINE,
        JOIN_INVITE_ALREADY_MEMBER_DETAIL,
        {
          primary: RECOVERY_LEAGUES,
          secondary: [RECOVERY_TRY_ANOTHER],
          showCommissionerSecondary: false,
        }
      );
    }
  }

  return buildRecovery(
    context === 'preview' ? 'invite_preview_failed' : 'invite_accept_failed',
    JOIN_BAD_INVITE_HEADLINE,
    context === 'preview'
      ? 'We could not load this invite right now. ' + JOIN_BAD_INVITE_ASK_COMMISSIONER
      : 'We could not join this league right now. ' + JOIN_BAD_INVITE_ASK_COMMISSIONER
  );
}

export { JOIN_COMMISSIONER_SECONDARY_NOTE };
